import { cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * The house tooltip (f-02064) — a short text label for an icon-only control.
 * WCAG 1.4.13-conformant: it shows on hover AND keyboard focus, stays while the
 * pointer is over the bubble itself, and Escape dismisses it without moving
 * focus. On touch, a long-press (500 ms) shows it, since there is no hover.
 *
 * It wires `aria-describedby` on the child by default; pass `describe={false}`
 * when the child's accessible name already IS the tooltip text (an icon-only
 * pin with `aria-label` = its name), so a screen reader does not say it twice.
 *
 * The child must be a single element that accepts the usual DOM handlers (a
 * <button>, a <span>). Portalled to <body> at `--z-popover`.
 */
export function Tooltip({ content, children, placement = 'top', describe = true, delay = 350 }: {
  content: ReactNode;
  children: ReactElement;
  placement?: 'top' | 'bottom';
  describe?: boolean;
  /** Hover delay before showing (focus shows at once). */
  delay?: number;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const anchor = useRef<HTMLElement | null>(null);
  const timers = useRef<{ show?: number; hide?: number; press?: number }>({});
  const overBubble = useRef(false);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const clear = () => {
    window.clearTimeout(timers.current.show);
    window.clearTimeout(timers.current.hide);
    window.clearTimeout(timers.current.press);
  };
  useEffect(() => clear, []);

  const show = useCallback((el: HTMLElement | null, wait: number) => {
    clear();
    if (!el || content == null || content === '') return;
    anchor.current = el;
    timers.current.show = window.setTimeout(() => { setRect(el.getBoundingClientRect()); setOpen(true); }, wait);
  }, [content]);
  const hide = useCallback((wait = 80) => {
    clear();
    timers.current.hide = window.setTimeout(() => { if (!overBubble.current) setOpen(false); }, wait);
  }, []);

  // Escape dismisses (without stealing it from anything else that is open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('scroll', onScroll, true); };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !rect) { setPos(null); return; }
    const b = bubbleRef.current;
    const w = b?.offsetWidth ?? 0;
    const h = b?.offsetHeight ?? 0;
    const vw = window.innerWidth;
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - w / 2, vw - w - 8));
    const above = rect.top - h - 6;
    const top = placement === 'top' && above > 8 ? above : rect.bottom + 6;
    setPos((prev) => (prev && prev.left === left && prev.top === top ? prev : { left, top }));
  }, [open, rect, placement]);

  if (!isValidElement(children)) return children as unknown as ReactElement;
  const child = children as ReactElement<Record<string, unknown>>;
  const p = child.props as Record<string, ((e: unknown) => void) | undefined>;
  const chain = (name: string, fn: (e: never) => void) => (e: unknown) => { p[name]?.(e); fn(e as never); };

  const trigger = cloneElement(child, {
    ...(describe && open ? { 'aria-describedby': id } : {}),
    onMouseEnter: chain('onMouseEnter', (e: React.MouseEvent<HTMLElement>) => show(e.currentTarget, delay)),
    onMouseLeave: chain('onMouseLeave', () => hide()),
    onFocus: chain('onFocus', (e: React.FocusEvent<HTMLElement>) => show(e.currentTarget, 0)),
    onBlur: chain('onBlur', () => hide(0)),
    onPointerDown: chain('onPointerDown', (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'touch') return;
      const el = e.currentTarget;
      clear();
      timers.current.press = window.setTimeout(() => { anchor.current = el; setRect(el.getBoundingClientRect()); setOpen(true); }, 500);
    }),
    onPointerUp: chain('onPointerUp', (e: React.PointerEvent<HTMLElement>) => {
      if (e.pointerType !== 'touch') return;
      window.clearTimeout(timers.current.press);
      if (open) timers.current.hide = window.setTimeout(() => setOpen(false), 1500);
    }),
    onPointerCancel: chain('onPointerCancel', () => window.clearTimeout(timers.current.press)),
  });

  return (
    <>
      {trigger}
      {open && createPortal(
        <div
          ref={bubbleRef}
          id={id}
          role="tooltip"
          className="fb-tooltip"
          onMouseEnter={() => { overBubble.current = true; window.clearTimeout(timers.current.hide); }}
          onMouseLeave={() => { overBubble.current = false; hide(); }}
          style={{
            position: 'fixed', left: pos?.left ?? -9999, top: pos?.top ?? -9999,
            zIndex: 'var(--z-popover)', visibility: pos ? 'visible' : 'hidden',
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}
